import { UnavailableIdJSONZ } from "_shared/_api/by-ids/by-ids-result";
import { logSchemaFallback } from "_shared/_utilities/log-schema-failure";
import z from "zod";

/**
 * The wire shapes of `work/policy-replacements` (`Work.PolicyReplacements` in the API). They are read
 * models: nothing here is built in the browser and sent back except the create command, so they stay
 * parsed shapes and the classes beside them (`PolicyReplacementPreview`, `PolicyReplacement`) carry
 * the rules.
 *
 * See `docs/design/proposals-negotiation/POLICY_REPLACEMENT_NEGOTIATION_DESIGN.md`.
 */

/** `PolicyReplacementScope`, by name. Mirrors `Work.Domain/PolicyReplacements/PolicyReplacementScope.cs`. */
export const POLICY_REPLACEMENT_SCOPES = [
  "Eligible",
  "RevisionInProgress",
  "Refused",
  "AlreadyReplacedHere",
  "NotHeld",
  "Ended",
  "NotYetHired",
] as const;

/** `PolicyReplacementProgress`, by name. Mirrors `Work.API/PolicyReplacements/PolicyReplacementContracts.cs`. */
export const POLICY_REPLACEMENT_PROGRESSES = [
  "Pending",
  "Waiting",
  "NeedsAPerson",
  "Stopped",
  "Withdrawn",
  "AlreadyReplaced",
  "InNegotiation",
  "Adopted",
  "NotAdopted",
  "Declined",
] as const;

/**
 * What a scope or a progress this bundle does not know becomes.
 *
 * The frontend and the API deploy separately, so a cached bundle meets names the API has started
 * sending. Failing the whole page over one row would hide every other agreement; naming the row as
 * something we cannot read, and never offering to act on it, costs nothing.
 */
export const UNRECOGNIZED = "Unrecognized";

export type PolicyReplacementScope = (typeof POLICY_REPLACEMENT_SCOPES)[number] | typeof UNRECOGNIZED;
export type PolicyReplacementProgress = (typeof POLICY_REPLACEMENT_PROGRESSES)[number] | typeof UNRECOGNIZED;

const ScopeZ = z.enum([...POLICY_REPLACEMENT_SCOPES, UNRECOGNIZED]).catch(({ value }) => {
  logSchemaFallback({ schemaName: "PolicyReplacementSchema", field: "scope", value, fallback: UNRECOGNIZED });
  return UNRECOGNIZED;
});

const ProgressZ = z.enum([...POLICY_REPLACEMENT_PROGRESSES, UNRECOGNIZED]).catch(({ value }) => {
  logSchemaFallback({ schemaName: "PolicyReplacementSchema", field: "progress", value, fallback: UNRECOGNIZED });
  return UNRECOGNIZED;
});

export const PolicyReplacementPreviewAgreementJSONZ = z.object({
  workAgreementId: z.string(),
  addedWorkAgreementId: z.string().nullish(),
  name: z.string(),
  vendorLegalEntityId: z.string().nullish(),
  replacedDocumentId: z.string(),
  scope: ScopeZ,
  openRevisionProposalId: z.string().nullish(),
  replacingDocumentId: z.string().nullish(),
});
export type PolicyReplacementPreviewAgreementJSON = z.infer<typeof PolicyReplacementPreviewAgreementJSONZ>;

export const PolicyHoldingNegotiationJSONZ = z.object({
  proposalId: z.string(),
  name: z.string(),
});
export type PolicyHoldingNegotiationJSON = z.infer<typeof PolicyHoldingNegotiationJSONZ>;

export const PolicyReplacementPreviewJSONZ = z.object({
  replacedDocumentId: z.string(),
  agreements: z.array(PolicyReplacementPreviewAgreementJSONZ),
  firstNegotiations: z.array(PolicyHoldingNegotiationJSONZ),
  unavailable: z.array(UnavailableIdJSONZ),
});
export type PolicyReplacementPreviewJSON = z.infer<typeof PolicyReplacementPreviewJSONZ>;

/** An agreement, and the client policy on it the replacement replaces. `PolicyReplacementAddition`. */
export type PolicyReplacementAgreementJSON = {
  workAgreementId: string;
  replacedDocumentId: string;
};

export type PolicyReplacementPreviewRequestJSON = {
  replacedDocumentId: string;
  additions: PolicyReplacementAgreementJSON[];
};

export type CreatePolicyReplacementCommandJSON = {
  replacedDocumentId: string;
  replacementDocumentId: string;
  reason: string;
  agreements: PolicyReplacementAgreementJSON[];
};

export const PolicyReplacementCreatedJSONZ = z.object({
  id: z.string().nullish(),
  targets: z.array(
    z.object({
      targetId: z.string(),
      selectedWorkAgreementId: z.string(),
      workAgreementId: z.string(),
    }),
  ),
  notReplaceable: z.array(z.object({ workAgreementId: z.string(), scope: ScopeZ })),
  unavailable: z.array(UnavailableIdJSONZ),
});
export type PolicyReplacementCreatedJSON = z.infer<typeof PolicyReplacementCreatedJSONZ>;

export const PolicyReplacementTargetJSONZ = z.object({
  targetId: z.string(),
  selectedWorkAgreementId: z.string(),
  workAgreementId: z.string(),
  progress: ProgressZ,
  revisionProposalId: z.string().nullish(),
  reason: z.string().nullish(),
});
export type PolicyReplacementTargetJSON = z.infer<typeof PolicyReplacementTargetJSONZ>;

export const PolicyReplacementJSONZ = z.object({
  id: z.string(),
  replacedDocumentId: z.string(),
  replacementDocumentId: z.string(),
  reason: z.string(),
  requestedByUserId: z.string(),
  createdDate: z.string(),
  targets: z.array(PolicyReplacementTargetJSONZ),
  counts: z.record(z.string(), z.number()),
});
export type PolicyReplacementJSON = z.infer<typeof PolicyReplacementJSONZ>;
